import { RadioInput } from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useKlaviyoCRUDActions } from '@mm/state/actions/KlaviyoCRUDActions';
import { ConnectionModel } from '@mm/state/model/ConnectionModel';
import { selectKlaviyoConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { AudienceSimpleSelect } from '@mm/stateful-components';
import { Switch } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { KlaviyoApiKeyConnection } from './KlaviyoApiKeyConnection';
import { KlaviyoConnectionModeSelector } from './KlaviyoConnectionModeSelector';
import { Help } from './KlaviyoHelpLink';
import { KlaviyoSetupMode } from './KlaviyoIntegrations';
import styles from './KlaviyoIntegrations.module.css';
import { KlaviyoListSelect } from './KlaviyoListSelect/KlaviyoListSelect';
import { KlaviyoOauthTokenConnection } from './KlaviyoOauthTokenConnection';

export const KlaviyoSettings = (props: {
  mode: KlaviyoSetupMode;
  onSelectMode: (mode: KlaviyoSetupMode) => void;
  showUpgradeModal: (onSuccessBilling?: () => void) => void;
  hasNativeIntegrationsFeature: boolean | undefined;
}) => {
  const { mode, onSelectMode, showUpgradeModal, hasNativeIntegrationsFeature } =
    props;
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const klaviyoConnections = useSelector(selectKlaviyoConnections);
  const klaviyoConnection = klaviyoConnections?.[0];

  const [radioValue, setRadioValue] = useState<'all' | 'audience'>(
    klaviyoConnection?.audienceId || !klaviyoConnection ? 'audience' : 'all',
  );

  const helpLink =
    'https://customers.ai/help/l/en/article/n663a8hz34-klaviyo-integration';

  const { update, retrieveById } = useConnectionCRUDActions();
  const { getLists } = useKlaviyoCRUDActions();
  const forceGetLists = (webhookId: number) => getLists(webhookId, true);

  const updateWebhook = async (connection: Partial<ConnectionModel>) => {
    if (!klaviyoConnection || !hasNativeIntegrationsFeature) return;
    await update(activeFbPageId, {
      id: klaviyoConnection.id,
      ...connection,
    });
  };

  const getKlaviyoConnection = async () => {
    if (klaviyoConnection) {
      retrieveById(activeFbPageId, klaviyoConnection.id);
    }
  };

  const retrieveKlaviyoLists = () => {
    if (
      (klaviyoConnection?.klaviyoAccessToken ||
        klaviyoConnection?.klaviyoOauthToken) &&
      hasNativeIntegrationsFeature
    ) {
      getLists(klaviyoConnection.id);
    }
  };

  useEffect(() => {
    getKlaviyoConnection();
    retrieveKlaviyoLists();
  }, []);

  const controlsDisabled =
    (!klaviyoConnection?.klaviyoAccessToken &&
      !klaviyoConnection?.klaviyoOauthToken) ||
    !hasNativeIntegrationsFeature;

  return (
    <div className={styles.settingsContent}>
      {mode === null && (
        <KlaviyoConnectionModeSelector
          helpLink={helpLink}
          onSelectMode={(mode: KlaviyoSetupMode) => onSelectMode(mode)}
        />
      )}
      {mode === 'oauth' && (
        <KlaviyoOauthTokenConnection
          activeFbPageId={activeFbPageId}
          hasNativeIntegrationsFeature={hasNativeIntegrationsFeature}
          helpLink={helpLink}
          connection={klaviyoConnection}
          getLists={forceGetLists}
          showUpgradeModal={showUpgradeModal}
        />
      )}
      {mode === 'api_key' && (
        <KlaviyoApiKeyConnection
          activeFbPageId={activeFbPageId}
          hasNativeIntegrationsFeature={hasNativeIntegrationsFeature}
          helpLink={helpLink}
          connection={klaviyoConnection}
          getLists={forceGetLists}
          showUpgradeModal={showUpgradeModal}
        />
      )}
      <div
        className={classNames(styles.box, {
          [styles.boxDisabled]: controlsDisabled,
        })}
      >
        <div className={styles.boxWithHelp}>
          <div className={classNames(styles.heading, styles.headingWithSwitch)}>
            Contact sync
            <Switch
              defaultChecked={
                klaviyoConnection ? klaviyoConnection?.active : true
              }
              checkedChildren="Yes"
              unCheckedChildren="No"
              className={styles.switch}
              onChange={ok => updateWebhook({ active: ok })}
            />
          </div>
          <Help link={helpLink} />
        </div>
        <div className={styles.radioInputs}>
          <RadioInput
            name="all"
            value="all"
            label="Sync all Customers.ai contacts to Klaviyo"
            onChange={() => {
              setRadioValue('all');
              updateWebhook({ audienceId: null });
            }}
            checked={radioValue === 'all'}
          />
          <RadioInput
            name="audience"
            value="audience"
            label="Choose an audience to sync from "
            onChange={() => setRadioValue('audience')}
            checked={radioValue === 'audience'}
          />
          {radioValue === 'audience' && (
            <AudienceSimpleSelect
              className={styles.audienceSelect}
              selectedAudienceId={klaviyoConnection?.audienceId || undefined}
              onChange={audienceId => updateWebhook({ audienceId })}
            />
          )}
        </div>
        <div className={styles.boxContent}>
          <div className={classNames(styles.heading, styles.fontSizeNormal)}>
            Which Klaviyo list do you want to sync leads to?
          </div>

          <div className={styles.selectWrapper}>
            <KlaviyoListSelect
              selectedConnection={klaviyoConnection}
              onChange={v => updateWebhook(v)}
              className={styles.listSelect}
            />
            <PrimaryButton
              className="integrations__header-add-button-secondary"
              color="transparent-no-shadow"
              onClick={retrieveKlaviyoLists}
            >
              Refresh
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};
